import React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingRow,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  isNamedPreset,
  parsePresetSelection,
  presetSelectionValue,
  writeNamedPreset,
} from "../utils/presetVocabulary"

interface MetricPresetControlProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
  label?: string
  rawKeys: string[]
  preserveRawOnNamed?: boolean
}

export const MetricPresetControl: React.FC<MetricPresetControlProps> = ({
  metricRecordData,
  onChange,
  label = "Detection Preset",
  rawKeys,
  preserveRawOnNamed = false,
}) => {
  const metadata = metricRecordData.metric_metadata
  const raw = metadata.preset
  const presetSelection = parsePresetSelection(raw)
  const hasRaw = rawKeys.some((key) => {
    const rawValue = metadata[key as keyof typeof metadata]
    return rawValue !== undefined && rawValue !== null
  })
  const isUnknown = presetSelection.kind === "unknown"
  const isCustom =
    presetSelection.kind === "custom" ||
    (hasRaw && !isUnknown && (!preserveRawOnNamed || raw === undefined))
  const value = isCustom ? "custom" : presetSelectionValue(presetSelection)

  const handleChange = (next: string) => {
    if (!isNamedPreset(next)) {
      return
    }
    const nextMetadata = preserveRawOnNamed
      ? { ...metadata, preset: next }
      : writeNamedPreset(metadata, "preset", undefined, next, rawKeys)
    onChange({ ...metricRecordData, metric_metadata: nextMetadata })
  }

  return (
    <SettingRow
      label={<span className="text-xs font-medium">{label}</span>}
      stacked
    >
      <Select value={value} onValueChange={handleChange}>
        <SelectTrigger aria-label={label} className="h-7 text-xs">
          <SelectValue placeholder="Select preset" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="strict">Strict</SelectItem>
          <SelectItem value="normal">Normal</SelectItem>
          <SelectItem value="lenient">Lenient</SelectItem>
          {isCustom && (
            <SelectItem value="custom" disabled>
              Custom
            </SelectItem>
          )}
          {isUnknown && (
            <SelectItem value="unknown" disabled>
              {presetSelection.label}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </SettingRow>
  )
}
